<template>
  <div class="fm-file-select mb-1">
    <div class="fm-media-container d-flex flex-column flex-sm-row align-items-stretch w-100 p-1 border rounded">
      <!-- پیش‌نمایش تصویر یا ویدئو -->
      <div
        v-if="!hidePreview"
        class="fm-media-preview-wrapper flex-shrink-0"
      >
        <!-- پیش‌نمایش تصویر -->
        <div
          v-if="type === 'img'"
          class="fm-media-preview position-relative rounded overflow-hidden d-flex align-items-center justify-content-center border"
          :class="{ 'cursor-pointer': cleanValue && !imgError, 'border-warning bg-light-warning': imgError, 'border-dashed': !cleanValue }"
          :title="cleanValue && !imgError ? 'برای مشاهده بزرگتر تصویر کلیک کنید' : 'برای انتخاب تصویر کلیک کنید'"
          @click="onThumbnailClick"
        >
          <template v-if="cleanValue && !imgError">
            <img
              :src="cleanValue"
              class="w-100 h-100 fm-media-img"
              alt="پیش‌نمایش تصویر"
              @error="onImgError"
              @load="onImgLoad"
            >
            <div class="fm-media-overlay d-flex align-items-center justify-content-center">
              <feather-icon
                icon="EyeIcon"
                size="20"
                class="text-white"
              />
            </div>
          </template>
          <div
            v-else
            class="d-flex flex-column align-items-center justify-content-center text-center p-50 w-100 h-100 cursor-pointer"
          >
            <feather-icon
              :icon="imgError ? 'AlertCircleIcon' : 'ImageIcon'"
              size="24"
              :class="imgError ? 'text-warning mb-25' : 'text-muted mb-25'"
            />
            <span
              v-if="imgError"
              class="text-warning font-small-1 font-weight-bold"
              style="font-size: 10px; line-height: 1.1;"
            >یافت نشد</span>
            <span
              v-else
              class="text-muted font-small-1"
              style="font-size: 11px; line-height: 1.1;"
            >انتخاب تصویر</span>
          </div>
        </div>

        <!-- پیش‌نمایش ویدئو -->
        <div
          v-else-if="type === 'video'"
          class="fm-media-preview position-relative rounded overflow-hidden d-flex align-items-center justify-content-center border bg-secondary"
          :class="{ 'cursor-pointer': cleanValue }"
          title="برای پخش ویدئو کلیک کنید"
          @click="openPreview"
        >
          <feather-icon
            icon="PlayCircleIcon"
            size="32"
            class="text-white"
          />
        </div>
      </div>

      <!-- بدنه اطلاعات و دکمه‌های عملیات -->
      <div class="fm-media-content d-flex flex-column justify-content-between flex-grow-1">
        <!-- بالا: عنوان و توضیحات -->
        <div>
          <label
            v-if="label"
            class="font-weight-bolder mb-25 text-body d-block"
            style="font-size: 0.95rem; line-height: 1.2;"
          >
            {{ label }}
          </label>

          <p
            v-if="description"
            class="text-muted font-small-2 mb-25"
            style="line-height: 1.3;"
          >
            {{ description }}
          </p>

          <!-- نام و عنوان فایل انتخابی (به جای آدرس قبلی) -->
          <div
            v-if="fileName"
            class="fm-file-name-tag d-inline-flex align-items-center my-25 mw-100"
            dir="ltr"
            :title="cleanValue"
          >
            <feather-icon
              :icon="type === 'video' ? 'FilmIcon' : 'FileTextIcon'"
              size="13"
              class="mr-50 text-primary flex-shrink-0"
            />
            <span
              class="font-small-2 font-sans text-truncate font-weight-bold"
              style="max-width: calc(100% - 24px);"
            >
              {{ fileName }}
            </span>
          </div>
        </div>

        <!-- پایین: نوار دکمه‌های عملیات -->
        <div
          class="fm-media-actions d-flex flex-wrap align-items-center mt-25"
          style="gap: 8px;"
        >
          <!-- دکمه انتخاب یا تغییر فایل -->
          <b-button
            v-ripple.400="'rgba(115, 103, 240, 0.15)'"
            :variant="cleanValue ? 'outline-primary' : 'primary'"
            size="sm"
            class="btn-sm py-25 px-75 d-inline-flex align-items-center"
            @click="focus"
          >
            <feather-icon
              :icon="cleanValue ? 'RefreshCwIcon' : 'FolderPlusIcon'"
              size="14"
              class="mr-50"
            />
            <span>{{ cleanValue ? 'تغییر فایل' : 'انتخاب از فایل‌منجر' }}</span>
          </b-button>

          <!-- دکمه مشاهده -->
          <b-button
            v-if="cleanValue && !imgError"
            v-ripple.400="'rgba(115, 103, 240, 0.15)'"
            variant="flat-primary"
            size="sm"
            class="btn-sm py-25 px-75 d-inline-flex align-items-center"
            @click="openPreview"
          >
            <feather-icon
              icon="EyeIcon"
              size="14"
              class="mr-50"
            />
            <span>مشاهده</span>
          </b-button>

          <!-- دکمه کپی لینک -->
          <b-button
            v-if="cleanValue"
            v-ripple.400="'rgba(108, 117, 125, 0.15)'"
            variant="flat-secondary"
            size="sm"
            class="btn-sm py-25 px-75 d-inline-flex align-items-center"
            title="کپی آدرس مستقیم فایل"
            @click="copyLink"
          >
            <feather-icon
              icon="CopyIcon"
              size="14"
              class="mr-50"
            />
            <span>کپی لینک</span>
          </b-button>

          <!-- دکمه حذف -->
          <b-button
            v-if="cleanValue"
            v-ripple.400="'rgba(234, 84, 85, 0.15)'"
            variant="flat-danger"
            size="sm"
            class="btn-sm py-25 px-75 d-inline-flex align-items-center"
            @click="clearFile"
          >
            <feather-icon
              icon="Trash2Icon"
              size="14"
              class="mr-50"
            />
            <span>حذف</span>
          </b-button>
        </div>
      </div>
    </div>

    <!-- مودال پیش‌نمایش تصویر -->
    <b-modal
      v-model="showImgModal"
      title="مشاهده تصویر"
      hide-footer
      centered
      size="lg"
    >
      <div class="text-center p-1">
        <img
          :src="cleanValue"
          style="max-width: 100%; max-height: 70vh; object-fit: contain; border-radius: 6px;"
          alt="تصویر انتخابی"
          @error="onImgError"
        >
        <div class="mt-1 d-flex justify-content-center align-items-center">
          <b-button
            variant="outline-secondary"
            size="sm"
            class="mr-50"
            :href="cleanValue"
            target="_blank"
          >
            <feather-icon
              icon="ExternalLinkIcon"
              size="14"
              class="mr-25"
            />
            <span>باز کردن در تب جدید</span>
          </b-button>
          <b-button
            variant="flat-primary"
            size="sm"
            @click="copyLink"
          >
            <feather-icon
              icon="CopyIcon"
              size="14"
              class="mr-25"
            />
            <span>کپی لینک</span>
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- مودال پخش ویدئو -->
    <b-modal
      v-model="showModal"
      title="نمایش ویدئو"
      hide-footer
      centered
    >
      <div class="d-flex justify-content-center">
        <video
          style="border-radius: 0.357rem; max-width: 100%;"
          height="250"
          controls
        >
          <source :src="cleanValue">
        </video>
      </div>
    </b-modal>

    <!-- پنجره مودال فایل‌منجر -->
    <file-manager-modal
      v-if="visible"
      v-model="selfValue"
      :show.sync="visible"
      :entity="entity"
      :selectable="selectable"
      :editable="editable"
      @select="val => $emit('select', val)"
    />
  </div>
</template>

<script>
import FileManagerModal from '@/views/menu/file-manager/components/fileManagerModal.vue'

export default {
  name: 'FmFileSelect',
  components: { FileManagerModal },
  props: {
    value: { type: String, default: '' },
    label: { type: String, default: '' },
    description: { type: String, default: '' },
    entity: { type: [String, Number], default: 0 },
    selectable: { type: Boolean, default: true },
    editable: { type: Boolean, default: true },
    hidePreview: { type: Boolean, default: false },
    type: { type: String, default: 'img' },
  },
  data() {
    return {
      visible: false,
      showModal: false,
      showImgModal: false,
      imgError: false,
    }
  },
  computed: {
    cleanValue() {
      if (!this.value) return ''
      return String(this.value)
        .replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d))
        .replace(/[٠-٩]/g, d => '٠١٢٣٤٥٦٧٨٩'.indexOf(d))
        .trim()
    },
    selfValue: {
      get() {
        return this.value
      },
      set(newValue) {
        this.$emit('input', newValue)
      },
    },
    fileName() {
      if (!this.cleanValue) return ''
      try {
        const cleanPath = this.cleanValue.split('?')[0].split('#')[0]
        const parts = cleanPath.split('/')
        const name = parts[parts.length - 1]
        return decodeURIComponent(name) || cleanPath
      } catch (e) {
        return this.cleanValue
      }
    },
  },
  watch: {
    value() {
      this.imgError = false
    },
  },
  methods: {
    focus() {
      this.visible = true
    },
    onThumbnailClick() {
      if (this.cleanValue && !this.imgError) {
        this.openPreview()
      } else {
        this.focus()
      }
    },
    onImgError() {
      this.imgError = true
    },
    onImgLoad() {
      this.imgError = false
    },
    openPreview() {
      if (!this.selfValue) return
      if (this.type === 'video') {
        this.showModal = true
      } else {
        this.showImgModal = true
      }
    },
    clearFile() {
      this.selfValue = ''
      this.imgError = false
    },
    copyLink() {
      if (!this.cleanValue) return
      const text = this.cleanValue
      if (this.$copyText) {
        this.$copyText(text)
          .then(() => {
            this.showToast('آدرس فایل با موفقیت کپی شد', 'success')
          })
          .catch(() => {
            this.showToast('خطا در کپی آدرس', 'warning')
          })
      } else if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(() => {
          this.showToast('آدرس فایل با موفقیت کپی شد', 'success')
        })
      }
    },
    showToast(msg, variant = 'success') {
      if (this.$bvToast) {
        this.$bvToast.toast(msg, {
          title: 'پیام سیستم',
          variant,
          solid: true,
          noCloseButton: true,
          autoHideDelay: 2000,
        })
      }
    },
  },
}
</script>

<style lang="scss">
.fm-file-select {
  width: 100%;

  .fm-media-container {
    gap: 1.25rem;
    border-color: #ebe9f1 !important;
    transition: border-color 0.15s ease-in-out;

    &:hover {
      border-color: #d8d6de !important;
    }
  }

  .fm-media-preview-wrapper {
    margin-left: 1.25rem;

    [dir='ltr'] & {
      margin-left: 0;
      margin-right: 1.25rem;
    }

    @media (max-width: 575.98px) {
      margin-left: 0 !important;
      margin-right: 0 !important;
      margin-bottom: 0.75rem;
    }
  }

  .fm-media-preview {
    width: 100px;
    height: 100px;
    min-width: 100px;
    min-height: 100px;
    background-color: #fafafc;
    position: relative;

    &.border-dashed {
      border-style: dashed !important;
      border-width: 1.5px !important;
      border-color: #d8d6de;

      &:hover {
        border-color: #7367f0;
        background-color: #f4f3fd;
      }
    }
  }

  .fm-media-content {
    min-height: 100px;
  }

  .fm-file-name-tag {
    background-color: rgba(115, 103, 240, 0.08);
    border: 1px solid rgba(115, 103, 240, 0.18);
    border-radius: 6px;
    padding: 4px 10px;
    max-width: 100%;
    cursor: default;
    transition: all 0.2s ease-in-out;

    span {
      color: #5e5873;
      transition: color 0.15s ease-in-out;
    }

    &:hover {
      background-color: rgba(115, 103, 240, 0.14);
      border-color: rgba(115, 103, 240, 0.35);

      span {
        color: #7367f0;
      }
    }
  }

  .fm-media-img {
    object-fit: cover;
    display: block;
  }

  .fm-media-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.45);
    opacity: 0;
    transition: opacity 0.2s ease-in-out;
  }

  .fm-media-preview:hover .fm-media-overlay {
    opacity: 1;
  }
}
</style>
